import { useEffect, useMemo, useState } from "react";
import t from "@/lang/lang";
import {
  useQpmMonitorServiceDefaultsListQuery,
  useQpmMonitorServiceDefaultsPatchMutation,
} from "@/services/backend/qpm/monitors/api/monitor-api";
import {
  MonitorIoTClass,
  MonitorType,
} from "@/services/backend/qpm/types/enums";
import { MonitorTypeUtil } from "@/services/backend/qpm/lib/monitor-type-util";
import { MonitorIoTClassUtil } from "@/services/backend/qpm/lib/monitor-iot-class-util";
import { resolveMonitorDefaultTimes } from "@/services/backend/qpm/monitors/lib/default-service-times";
import { Input } from "@/shared/components/ui/input";
import { Button } from "@/shared/components/ui/button";
import { Pencil, Save, X } from "lucide-react";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Skeleton } from "@/shared/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import {
  TimeNumberInput,
  TimeUnit,
} from "@/shared/components/inputs/time-number-input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";

type MonitorDefaultsRowDefinition = {
  monitorType: MonitorType;
  iotClass?: MonitorIoTClass;
  checkpointVisual?: boolean;
};

type MonitorDefaultsRowValues = {
  installationTime: number;
  maintenanceTime: number;
  yearlyVisits: number;
};

const monitorDefaultsRows: MonitorDefaultsRowDefinition[] = [
  { monitorType: MonitorType.Checkpoint, checkpointVisual: false },
  { monitorType: MonitorType.Checkpoint, checkpointVisual: true },
  { monitorType: MonitorType.FlyExterminator },
  ...Object.values(MonitorIoTClass).map((iotClass) => ({
    monitorType: MonitorType.IoT,
    iotClass,
  })),
];

const toRowKey = ({
  monitorType,
  iotClass,
  checkpointVisual,
}: MonitorDefaultsRowDefinition) =>
  `${monitorType}:${iotClass ?? ""}:${checkpointVisual ? "visual" : ""}`;

const toMonitorLabel = ({
  monitorType,
  iotClass,
  checkpointVisual,
}: MonitorDefaultsRowDefinition) => {
  if (monitorType !== MonitorType.IoT || iotClass === undefined) {
    if (monitorType === MonitorType.Checkpoint && checkpointVisual) {
      return t("Visuelle Prüfpunkte");
    }
    return MonitorTypeUtil.toLongString(monitorType);
  }

  return `${MonitorTypeUtil.toLongString(monitorType)} ${MonitorIoTClassUtil.toLongString(iotClass)}`;
};

const clampVisits = (value: number) => {
  if (!Number.isFinite(value) || value < 0) return 0;
  return Math.round(value);
};

export function MonitorDefaultsCard() {
  const {
    data: serviceDefaults,
    isLoading,
    error,
  } = useQpmMonitorServiceDefaultsListQuery({});
  const [patchDefaults, patchState] =
    useQpmMonitorServiceDefaultsPatchMutation();
  const [draftByRow, setDraftByRow] = useState<
    Record<string, MonitorDefaultsRowValues>
  >({});
  const [savingRowKey, setSavingRowKey] = useState<string | null>(null);
  const [editingRowKey, setEditingRowKey] = useState<string | null>(null);

  const persistedByRow = useMemo(() => {
    const items = serviceDefaults?.items ?? [];
    const values: Record<string, MonitorDefaultsRowValues> = {};

    for (const row of monitorDefaultsRows) {
      const key = toRowKey(row);
      const selected = items.find(
        (item) =>
          item.monitorType === row.monitorType &&
          item.iotClass === row.iotClass &&
          item.checkpointVisual === Boolean(row.checkpointVisual),
      );
      const sameType = items.filter(
        (item) =>
          item.monitorType === row.monitorType &&
          item.checkpointVisual === Boolean(row.checkpointVisual),
      );
      const fallbackYearlyVisits =
        sameType.find((item) => item.iotClass === undefined)?.yearlyVisits ??
        sameType[0]?.yearlyVisits ??
        0;
      const times = resolveMonitorDefaultTimes(
        serviceDefaults,
        row.monitorType,
        row.iotClass,
        Boolean(row.checkpointVisual),
      );

      values[key] = {
        installationTime: times.install,
        maintenanceTime: times.maintain,
        yearlyVisits: clampVisits(
          selected?.yearlyVisits ?? fallbackYearlyVisits,
        ),
      };
    }

    return values;
  }, [serviceDefaults]);

  useEffect(() => {
    setDraftByRow(persistedByRow);
  }, [persistedByRow]);

  const updateDraft = (
    rowKey: string,
    patch: Partial<MonitorDefaultsRowValues>,
  ) => {
    setDraftByRow((prev) => {
      const current = prev[rowKey] ?? persistedByRow[rowKey];
      if (!current) return prev;
      return {
        ...prev,
        [rowKey]: {
          ...current,
          ...patch,
        },
      };
    });
  };

  const saveRow = async (row: MonitorDefaultsRowDefinition) => {
    const key = toRowKey(row);
    const current = draftByRow[key] ?? persistedByRow[key];
    if (!current) return;

    setSavingRowKey(key);
    try {
      await patchDefaults({
        monitorType: row.monitorType,
        ...(row.iotClass !== undefined ? { iotClass: row.iotClass } : {}),
        checkpointVisual: Boolean(row.checkpointVisual),
        installationTime: current.installationTime,
        maintenanceTime: current.maintenanceTime,
        yearlyVisits: clampVisits(current.yearlyVisits),
      }).unwrap();
      setEditingRowKey(null);
    } finally {
      setSavingRowKey(null);
    }
  };

  const cancelEdit = (row: MonitorDefaultsRowDefinition) => {
    const key = toRowKey(row);
    const persisted = persistedByRow[key];
    if (!persisted) return;
    setDraftByRow((prev) => ({
      ...prev,
      [key]: persisted,
    }));
    setEditingRowKey(null);
  };

  if (error) return <RTKQueryErrorAlert error={error} />;

  return (
    <div className="space-y-4">
      <div className="text-muted-foreground text-sm">
        {t(
          "Diese Grundwerte werden als Vorgabe beim Anlegen neuer Monitore verwendet.",
        )}
      </div>
      <div className="text-muted-foreground text-sm">
        {t(
          "Die Zeiten gelten für die Bestandsaufnahme in Anforderungen und nicht für Kalkulationszeiten.",
        )}
      </div>

      {patchState.error ? (
        <RTKQueryErrorAlert error={patchState.error} />
      ) : null}

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("Monitor")}</TableHead>
              <TableHead>{t("Installationszeit")}</TableHead>
              <TableHead>{t("Servicezeit")}</TableHead>
              <TableHead>{t("Einsätze/Jahr")}</TableHead>
              <TableHead className="text-right">{t("Bearbeiten")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading
              ? Array.from({ length: 6 }).map((_, idx) => (
                  <TableRow key={`monitor-default-skeleton-${idx}`}>
                    <TableCell>
                      <Skeleton className="h-5 w-56" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-8 w-36" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-8 w-36" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-8 w-24" />
                    </TableCell>
                    <TableCell className="text-right">
                      <Skeleton className="ml-auto h-8 w-24" />
                    </TableCell>
                  </TableRow>
                ))
              : monitorDefaultsRows.map((row) => {
                  const key = toRowKey(row);
                  const persisted = persistedByRow[key];
                  const draft = draftByRow[key] ?? persisted;
                  if (!persisted || !draft) return null;

                  const isDirty =
                    draft.installationTime !== persisted.installationTime ||
                    draft.maintenanceTime !== persisted.maintenanceTime ||
                    clampVisits(draft.yearlyVisits) !==
                      clampVisits(persisted.yearlyVisits);
                  const isSaving = savingRowKey === key;
                  const isEditing = editingRowKey === key;

                  return (
                    <TableRow key={key}>
                      <TableCell className="font-medium">
                        {toMonitorLabel(row)}
                      </TableCell>
                      <TableCell>
                        <TimeNumberInput
                          timeUnit={TimeUnit.Minutes}
                          value={draft.installationTime}
                          disabled={!isEditing}
                          onChange={(value) =>
                            updateDraft(key, { installationTime: value })
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TimeNumberInput
                          timeUnit={TimeUnit.Minutes}
                          value={draft.maintenanceTime}
                          disabled={!isEditing}
                          onChange={(value) =>
                            updateDraft(key, { maintenanceTime: value })
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          min={0}
                          step={1}
                          className="h-8 w-24 text-right"
                          disabled={!isEditing}
                          value={draft.yearlyVisits}
                          onChange={(e) =>
                            updateDraft(key, {
                              yearlyVisits: clampVisits(
                                Number.parseInt(e.target.value, 10),
                              ),
                            })
                          }
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {isEditing ? (
                            <>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      disabled={isSaving}
                                      onClick={() => cancelEdit(row)}
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{t("Abbrechen")}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      size="sm"
                                      variant="default"
                                      disabled={!isDirty || isSaving}
                                      onClick={() => saveRow(row)}
                                    >
                                      <Save className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{t("Speichern")}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </>
                          ) : (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setEditingRowKey(key)}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{t("Bearbeiten")}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
